import * as React from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, LayoutGroup } from 'framer-motion';
import { toast } from 'sonner';
import { Plus, Filter, FileText, Wallet, AlertCircle, ArrowUpRight, Receipt, CheckCircle2, Send, X, FileDown } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { Button } from '../../../components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDate, formatMoney } from '../../../design-system/format';
import { GlassCard, BentoGrid, Eyebrow, StatusChip, Avatar } from '../../../design-system/pds';
import { Sparkline, WaveStockChart } from '../../../design-system/charts';
import { PdfPreviewPanel } from '../components/PdfPreviewPanel';
import { invoicesApi } from '../api';
import type { Invoice, InvoiceStatus, RecurringInvoice, Client, DocumentType } from '../types';

const STATUS_OPTIONS: Array<InvoiceStatus | 'all'> = ['all', 'draft', 'sent', 'partial', 'paid', 'overdue', 'cancelled'];
const ESTIMATE_STATUS_OPTIONS: Array<InvoiceStatus | 'all'> = ['all', 'draft', 'sent', 'accepted', 'declined', 'cancelled'];
// Credit notes are born 'sent' (issued immediately); 'cancelled' is reachable via API.
const CREDIT_NOTE_STATUS_OPTIONS: Array<InvoiceStatus | 'all'> = ['all', 'sent', 'cancelled'];

export function InvoicesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isRecurringTab = location.pathname.endsWith('/recurring');
  // Estimates + credit notes live behind a search param so no new App.tsx route is needed.
  const tab = searchParams.get('tab');
  const isEstimatesTab = !isRecurringTab && tab === 'estimates';
  const isCreditNotesTab = !isRecurringTab && tab === 'credit-notes';
  const docType: DocumentType = isEstimatesTab ? 'estimate' : isCreditNotesTab ? 'credit_note' : 'invoice';
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';

  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      await invoicesApi.downloadExportCsv();
    } catch (err: any) {
      toast.error('Export failed', { description: err.message ?? String(err) });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full min-h-screen">
      <div className="flex-1 min-w-0">
        <PageHeader
          eyebrow={isRecurringTab ? 'Money · Invoices · Recurring'
            : isEstimatesTab ? 'Money · Invoices · Estimates'
            : isCreditNotesTab ? 'Money · Invoices · Credit notes'
            : 'Money · Invoices'}
          title={isEstimatesTab ? 'Estimates' : isCreditNotesTab ? 'Credit notes' : 'Invoices'}
          description={isRecurringTab
            ? 'Templates that run on a schedule.'
            : isEstimatesTab
              ? 'Quotes awaiting a yes — convert accepted ones to invoices in a click.'
              : isCreditNotesTab
                ? 'Refunds and adjustments issued against paid invoices.'
                : 'Outstanding, sent, and paid — all in one place.'}
          actions={isCreditNotesTab ? undefined : (
            <div className="flex items-center gap-2">
              {!isRecurringTab && !isEstimatesTab && (
                <Button size="default" variant="outline" className="gap-1.5" disabled={exporting} onClick={handleExport}>
                  <FileDown className="h-3.5 w-3.5" />
                  {exporting ? 'Exporting…' : 'Export to accounting'}
                </Button>
              )}
              <Link to={isRecurringTab
                ? '/dashboard/invoices/recurring/new'
                : isEstimatesTab
                  ? '/dashboard/invoices/new?type=estimate'
                  : '/dashboard/invoices/new'}>
                <Button data-tour="invoices.new" size="default" className="gap-1.5">
                  <Plus className="h-3.5 w-3.5" />
                  {isRecurringTab ? 'New recurring' : isEstimatesTab ? 'New estimate' : 'New invoice'}
                </Button>
              </Link>
            </div>
          )}
        />

        <div className="px-6 sm:px-8 lg:px-10">
          <LayoutGroup id="invoiceTabs">
            <div className="inline-flex items-center gap-0 p-1 rounded-full bg-white/55 dark:bg-white/[0.05] backdrop-blur-md ring-1 ring-white/40 dark:ring-white/10 shadow-pill dark:shadow-none mb-6">
              <TabPill active={!isRecurringTab && !isEstimatesTab && !isCreditNotesTab} onClick={() => navigate('/dashboard/invoices')}>One-off</TabPill>
              <TabPill active={isEstimatesTab} onClick={() => navigate('/dashboard/invoices?tab=estimates')}>Estimates</TabPill>
              <TabPill active={isCreditNotesTab} onClick={() => navigate('/dashboard/invoices?tab=credit-notes')}>Credit notes</TabPill>
              <TabPill active={isRecurringTab} onClick={() => navigate('/dashboard/invoices/recurring')}>Recurring</TabPill>
            </div>
          </LayoutGroup>
        </div>

        {isRecurringTab
          ? <RecurringList currency={currency} />
          : <InvoicesList key={docType} currency={currency} selectedId={selectedId} onSelect={setSelectedId} docType={docType} />}
      </div>

      {!isRecurringTab && (
        <aside className="w-[360px] 2xl:w-[480px] shrink-0 hidden xl:flex flex-col bg-white/45 dark:bg-white/[0.04] backdrop-blur-xl border-l border-white/40 dark:border-white/10">
          <PdfPreviewPanel invoiceId={selectedId} />
        </aside>
      )}
    </div>
  );
}

function TabPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'relative inline-flex items-center px-5 h-9 text-[12.5px] font-semibold transition-colors',
        active ? 'text-white' : 'text-ink/65 dark:text-white/65 hover:text-ink dark:hover:text-white',
      ].join(' ')}
    >
      {active && (
        <motion.span
          layoutId="invoiceTabsActive"
          className="absolute inset-0 -z-10 rounded-full bg-ink-900 shadow-cta-dark"
          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
        />
      )}
      <span className="relative">{children}</span>
    </button>
  );
}

/* ─────────────────────────── one-off invoices ─────────────────────────── */

const PAGE_SIZE = 50;

function InvoicesList({
  currency,
  selectedId,
  onSelect,
  docType,
}: {
  currency: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  docType: DocumentType;
}) {
  const navigate = useNavigate();
  const estimates = docType === 'estimate';
  const creditNotes = docType === 'credit_note';
  const [status, setStatus] = React.useState<InvoiceStatus | 'all'>('all');
  const [clientId, setClientId] = React.useState<string>('all');
  const [offset, setOffset] = React.useState(0);
  const statusOptions = estimates ? ESTIMATE_STATUS_OPTIONS : creditNotes ? CREDIT_NOTE_STATUS_OPTIONS : STATUS_OPTIONS;

  // Back to page 1 whenever a filter changes (docType remounts via key).
  React.useEffect(() => { setOffset(0); }, [status, clientId]);

  const path = React.useMemo(() => {
    const qs = new URLSearchParams();
    qs.set('document_type', docType);
    if (status !== 'all') qs.set('status', status);
    if (clientId !== 'all') qs.set('client_id', clientId);
    qs.set('limit', String(PAGE_SIZE));
    qs.set('offset', String(offset));
    return `/api/invoicing/invoices?${qs.toString()}`;
  }, [status, clientId, docType, offset]);

  // `total` is additive on the response (older shape was just { invoices }).
  const { data, loading, error, reload } = useApi<{ invoices: Invoice[]; total?: number }>(path);
  const clientsQ = useApi<{ clients: Client[] }>('/api/invoicing/clients');
  const invoices = data?.invoices ?? [];
  const total = data?.total ?? offset + invoices.length;

  const openDetail = (id: string) => navigate(`/dashboard/invoices/${id}`);
  const onRowClick = (id: string) => {
    // ≥lg the preview aside is visible → clicking selects for preview.
    // <lg there is no aside, so clicking a row navigates to the detail page.
    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
      onSelect(id);
    } else {
      openDetail(id);
    }
  };

  // --- Bulk selection -----------------------------------------------------
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = React.useState(false);

  // Drop any selected ids that left the current view (filter change / refresh).
  React.useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(invoices.map((i) => i.id));
      const next = new Set<string>();
      prev.forEach((id) => { if (visible.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [invoices]);

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const selectableIds = invoices.map((i) => i.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(selectableIds));

  async function runBulk(label: string, fn: (ids: string[]) => Promise<{ updated: number; skipped: Array<{ id: string; reason: string }> }>) {
    if (selected.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    try {
      const res = await fn(Array.from(selected));
      const skippedNote = res.skipped.length ? ` · ${res.skipped.length} skipped` : '';
      toast.success(`${label}: ${res.updated} updated${skippedNote}`);
      setSelected(new Set());
      reload();
    } catch (err: any) {
      toast.error(`${label} failed`, { description: err?.message ?? String(err) });
    } finally {
      setBulkBusy(false);
    }
  }

  const totalAmt   = invoices.reduce((s, r) => s + Number(r.total || 0), 0);
  const openSet    = (estimates || creditNotes)
    ? invoices.filter((r) => r.status === 'sent')
    : invoices.filter((r) => r.status === 'sent' || r.status === 'overdue' || r.status === 'partial');
  const openAmt    = openSet.reduce((s, r) => s + Number(r.total || 0), 0);
  const overdue    = estimates ? invoices.filter((r) => r.status === 'declined')
    : creditNotes ? invoices.filter((r) => r.status === 'cancelled')
    : invoices.filter((r) => r.status === 'overdue');
  const overdueAmt = overdue.reduce((s, r) => s + Number(r.total || 0), 0);
  const paid       = estimates ? invoices.filter((r) => r.status === 'accepted')
    : creditNotes ? []
    : invoices.filter((r) => r.status === 'paid');
  const paidAmt    = paid.reduce((s, r) => s + Number(r.total || 0), 0);

  const docNoun = estimates ? 'estimates' : creditNotes ? 'credit notes' : 'invoices';

  // Trend series: last 12 invoices' totals
  const trend = React.useMemo(
    () => invoices.slice(-12).map((r) => Number(r.total) || 0),
    [invoices],
  );

  return (
    <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
      {/* KPI bento */}
      <BentoGrid cols={12} className="">
        <GlassCard className="col-span-12" padding="p-7">
          <div className="flex items-start justify-between mb-3 gap-3">
            <div className="min-w-0">
              <Eyebrow className="mb-2">{estimates ? 'Total quoted' : creditNotes ? 'Total credited' : 'Total billed'}</Eyebrow>
              <p className="text-stat-lg tabular-nums font-bold text-ink dark:text-white leading-none truncate">{formatMoney(totalAmt, currency)}</p>
              <p className="text-[13px] text-ink/55 dark:text-white/55 mt-2">Across {invoices.length}{total > invoices.length ? ` of ${total}` : ''} {docNoun} in current view</p>
            </div>
            <div className="h-10 w-10 rounded-full bg-coral/10 flex items-center justify-center shrink-0">
              <FileText className="h-4 w-4 text-coral-600" />
            </div>
          </div>
          {trend.length >= 2 && (
            <WaveStockChart data={trend} height={70} filled />
          )}
        </GlassCard>
        <GlassCard className="col-span-6" padding="p-7">
          <div className="flex items-center justify-between mb-3 gap-2">
            <Eyebrow>{estimates ? 'Awaiting answer' : creditNotes ? 'Issued' : 'Outstanding'}</Eyebrow>
            <div className="h-9 w-9 rounded-full bg-coral/10 flex items-center justify-center shrink-0">
              <Wallet className="h-3.5 w-3.5 text-coral-600" />
            </div>
          </div>
          <p className="text-stat-md tabular-nums font-bold text-ink dark:text-white leading-none truncate">{formatMoney(openAmt, currency)}</p>
          <p className="text-[12.5px] text-ink/55 dark:text-white/55 mt-2">
            {creditNotes ? `${openSet.length} issued` : `${openSet.length} open · ${paid.length} ${estimates ? 'accepted' : 'paid'}`}
          </p>
        </GlassCard>
        <GlassCard className="col-span-6" padding="p-7">
          <div className="flex items-center justify-between mb-3 gap-2">
            <Eyebrow>{estimates ? 'Declined' : creditNotes ? 'Cancelled' : 'Overdue'}</Eyebrow>
            <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${overdue.length > 0 ? 'bg-rose-500/15' : 'bg-ink/5'}`}>
              <AlertCircle className={`h-3.5 w-3.5 ${overdue.length > 0 ? 'text-rose-600' : 'text-ink/45 dark:text-white/45'}`} />
            </div>
          </div>
          <p className={`text-stat-md tabular-nums font-bold leading-none truncate ${overdue.length > 0 ? 'text-rose-600' : 'text-ink dark:text-white'}`}>{formatMoney(overdueAmt, currency)}</p>
          <p className="text-[12.5px] text-ink/55 dark:text-white/55 mt-2">
            {overdue.length} {estimates ? `estimate${overdue.length === 1 ? '' : 's'} declined`
              : creditNotes ? `credit note${overdue.length === 1 ? '' : 's'} cancelled`
              : `invoice${overdue.length === 1 ? '' : 's'} late`}
          </p>
        </GlassCard>
      </BentoGrid>

      {/* Filters */}
      <section className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-full bg-white/65 dark:bg-white/[0.05] backdrop-blur-md shadow-pill dark:shadow-none text-[12px] font-semibold text-ink/55 dark:text-white/55">
          <Filter className="h-3 w-3" />
          Filter
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as any)}>
          <SelectTrigger className="h-10 w-auto min-w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((s) => (
              <SelectItem key={s} value={s}>
                {s === 'all' ? 'All statuses' : s === 'partial' ? 'Partially paid' : s[0].toUpperCase() + s.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="h-10 w-auto min-w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {(clientsQ.data?.clients ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {paidAmt > 0 && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-lime-neon text-ink rounded-full text-[11px] font-bold uppercase tracking-[0.10em] ml-auto">
            <Receipt className="h-3 w-3" />
            {formatMoney(paidAmt, currency)} {estimates ? 'accepted' : 'collected'}
          </span>
        )}
      </section>

      {/* Bulk action bar (credit notes have no bulk actions — born sent) */}
      {!creditNotes && selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl bg-ink-900 text-white shadow-cta-dark">
          <span className="text-[13px] font-semibold tabular-nums">
            {selected.size} selected
          </span>
          <div className="flex items-center gap-2 ml-auto">
            {!estimates && (
              <Button
                size="sm"
                variant="secondary"
                disabled={bulkBusy}
                className="gap-1.5"
                onClick={() => runBulk('Mark paid', (ids) => invoicesApi.bulkMarkPaid(ids))}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Mark paid
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              disabled={bulkBusy}
              className="gap-1.5"
              onClick={() => runBulk('Send', (ids) => invoicesApi.bulkSend(ids))}
            >
              <Send className="h-3.5 w-3.5" />
              Send
            </Button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Clear selection"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <GlassCard padding="p-0" className="overflow-hidden">
        {loading ? (
          <ListSkeleton />
        ) : error ? (
          <ErrorState
            title={`Couldn't load ${estimates ? 'estimates' : 'invoices'}`}
            description={error}
            onRetry={reload}
          />
        ) : invoices.length === 0 ? (
          <InvoicesEmptyState filtered={status !== 'all' || clientId !== 'all'} docType={docType} />
        ) : (
          <ul className="divide-y divide-ink/6">
            {invoices.length > 0 && !creditNotes && (
              <li className="flex items-center gap-3 px-5 py-2.5 bg-white/30 dark:bg-white/[0.04]">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Select all invoices"
                  className="h-4 w-4 rounded border-ink/25 text-coral-600 focus:ring-coral-500 cursor-pointer"
                />
                <span className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink/45 dark:text-white/45">
                  Select all
                </span>
              </li>
            )}
            {invoices.map((inv, i) => {
              const client = (clientsQ.data?.clients ?? []).find((c) => c.id === inv.client_id);
              const isSelected = inv.id === selectedId;
              const isChecked = selected.has(inv.id);
              return (
                <motion.li
                  key={inv.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.02, 0.18) }}
                  className={[
                    'flex items-center transition-colors',
                    isSelected ? 'bg-coral/8' : isChecked ? 'bg-coral/4' : 'hover:bg-white/40 dark:hover:bg-white/[0.05]',
                  ].join(' ')}
                >
                  {!creditNotes && (
                    <label
                      className="pl-5 py-4 flex items-center cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleOne(inv.id)}
                        aria-label={`Select invoice ${inv.invoice_number}`}
                        className="h-4 w-4 rounded border-ink/25 text-coral-600 focus:ring-coral-500 cursor-pointer"
                      />
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => onRowClick(inv.id)}
                    className={['flex-1 min-w-0 text-left flex items-center gap-4 pr-3 py-4', creditNotes ? 'pl-5' : 'pl-3'].join(' ')}
                  >
                    <Avatar name={client?.name} size="lg" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[14px] font-semibold text-ink dark:text-white truncate">
                          {client?.name ?? 'Unknown client'}
                        </span>
                        <span className="text-[11px] font-mono text-ink/45 dark:text-white/45">{inv.invoice_number}</span>
                      </div>
                      <p className="text-[12px] text-ink/55 dark:text-white/55 mt-0.5">
                        Issued {formatDate(inv.issued_at)} · Due {formatDate(inv.due_at)}
                      </p>
                    </div>
                    <div className="hidden sm:flex flex-col items-end gap-2">
                      <StatusChip status={inv.status}>
                        {inv.status === 'partial' ? 'Partially paid' : undefined}
                      </StatusChip>
                      <span className="text-[15px] font-bold tabular-nums text-ink dark:text-white leading-none">
                        {formatMoney(inv.total, inv.currency ?? currency)}
                      </span>
                    </div>
                  </button>
                  {/* ≥lg row-click selects for preview, so give an explicit door to the detail page. */}
                  <Link
                    to={`/dashboard/invoices/${inv.id}`}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Open ${inv.invoice_number}`}
                    title="Open detail page"
                    className="hidden lg:inline-flex h-9 w-9 mr-4 items-center justify-center rounded-full text-ink/45 dark:text-white/45 hover:text-ink dark:hover:text-white hover:bg-white/60 dark:hover:bg-white/[0.08] transition-colors shrink-0"
                  >
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </motion.li>
              );
            })}
          </ul>
        )}
      </GlassCard>

      {/* Pagination */}
      {!loading && !error && total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <span className="text-[12.5px] text-ink/55 dark:text-white/55 tabular-nums">
            Showing {total === 0 ? 0 : offset + 1}–{offset + invoices.length} of {total}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={offset + invoices.length >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── recurring ─────────────────────────── */

function RecurringList({ currency }: { currency: string }) {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi<{ recurring: RecurringInvoice[] }>('/api/invoicing/recurring');
  const rows = data?.recurring ?? [];

  return (
    <div className="px-6 sm:px-8 lg:px-10 pb-12">
      <GlassCard padding="p-0" className="overflow-hidden">
        {loading ? (
          <ListSkeleton />
        ) : error ? (
          <ErrorState title="Couldn't load recurring templates" description={error} onRetry={reload} />
        ) : rows.length === 0 ? (
          <RecurringEmptyState />
        ) : (
          <ul className="divide-y divide-ink/6">
            {rows.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/invoices/recurring/${r.id}/edit`)}
                  className="w-full text-left flex items-center gap-4 px-5 py-4 hover:bg-white/40 dark:hover:bg-white/[0.05] transition-colors"
                >
                  <div className="h-11 w-11 rounded-2xl bg-coral/15 flex items-center justify-center shrink-0">
                    <FileText className="h-4 w-4 text-coral-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[14px] font-semibold text-ink dark:text-white truncate">{r.name}</p>
                    <p className="text-[12px] text-ink/55 dark:text-white/55 mt-0.5">
                      Every {r.interval_count} {r.cadence.replace(/ly$/, '')}{r.interval_count > 1 ? 's' : ''} · next on {formatDate(r.next_run_at)}
                    </p>
                  </div>
                  <StatusChip status={r.is_active ? 'active' : 'inactive'}>
                    {r.is_active ? 'Active' : 'Paused'}
                  </StatusChip>
                </button>
              </li>
            ))}
          </ul>
        )}
      </GlassCard>
      <span hidden>{currency}</span>
    </div>
  );
}

/* ─────────────────────────── inner bits ─────────────────────────── */

function ListSkeleton() {
  return (
    <ul className="divide-y divide-ink/6">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="h-11 w-11 rounded-2xl bg-pearl-200 dark:bg-white/10 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-pearl-200 dark:bg-white/10 animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-pearl-100 dark:bg-white/[0.07] animate-pulse" />
          </div>
          <div className="h-3 w-20 rounded bg-pearl-200 dark:bg-white/10 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}

function InvoicesEmptyState({ filtered, docType }: { filtered: boolean; docType: DocumentType }) {
  const estimates = docType === 'estimate';
  const creditNotes = docType === 'credit_note';
  if (filtered) {
    return (
      <div className="py-16 px-8 text-center">
        <p className="text-[14px] font-semibold text-ink dark:text-white">No {estimates ? 'estimates' : creditNotes ? 'credit notes' : 'invoices'} match those filters</p>
        <p className="text-[13px] text-ink/55 dark:text-white/55 mt-1">Try clearing them, or change the status / client picker above.</p>
      </div>
    );
  }
  if (creditNotes) {
    return (
      <div className="py-16 px-8 text-center max-w-md mx-auto">
        <div className="mx-auto mb-5 h-16 w-16 rounded-3xl bg-coral/12 flex items-center justify-center">
          <Receipt className="h-6 w-6 text-coral-600" strokeWidth={1.6} />
        </div>
        <p className="text-[20px] font-semibold text-ink dark:text-white tracking-[-0.015em]">No credit notes yet</p>
        <p className="text-[14px] text-ink/60 dark:text-white/60 mt-2 leading-relaxed">
          Credit notes reverse part or all of a paid invoice — a refund, a return, an adjustment.
          Issue one from the invoice's detail page.
        </p>
        <div className="mt-6">
          <Link to="/dashboard/invoices">
            <Button variant="secondary" className="gap-1.5">Go to invoices <ArrowUpRight className="h-3.5 w-3.5" /></Button>
          </Link>
        </div>
      </div>
    );
  }
  if (estimates) {
    return (
      <div className="py-16 px-8 text-center max-w-md mx-auto">
        <div className="mx-auto mb-5 h-16 w-16 rounded-3xl bg-coral/12 flex items-center justify-center">
          <FileText className="h-6 w-6 text-coral-600" strokeWidth={1.6} />
        </div>
        <p className="text-[20px] font-semibold text-ink dark:text-white tracking-[-0.015em]">Quote it before you bill it</p>
        <p className="text-[14px] text-ink/60 dark:text-white/60 mt-2 leading-relaxed">
          Estimates get their own EST numbers. When the client says yes, one click converts the estimate into a draft invoice.
        </p>
        <div className="mt-6">
          <Link to="/dashboard/invoices/new?type=estimate">
            <Button className="gap-1.5"><Plus className="h-3.5 w-3.5" /> New estimate</Button>
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="py-16 px-8 text-center max-w-md mx-auto">
      <div className="mx-auto mb-5 h-16 w-16 rounded-3xl bg-coral/12 flex items-center justify-center">
        <FileText className="h-6 w-6 text-coral-600" strokeWidth={1.6} />
      </div>
      <p className="text-[20px] font-semibold text-ink dark:text-white tracking-[-0.015em]">Send your first invoice</p>
      <p className="text-[14px] text-ink/60 dark:text-white/60 mt-2 leading-relaxed">
        Add a client, drop in your services, hit send. A polished PDF lands in their inbox, with a Stripe pay link baked in.
      </p>
      <div className="mt-6 flex items-center justify-center gap-2.5 flex-wrap">
        <Link to="/dashboard/invoices/new">
          <Button className="gap-1.5"><Plus className="h-3.5 w-3.5" /> New invoice</Button>
        </Link>
        <Link to="/dashboard/clients/new">
          <Button variant="secondary" className="gap-1.5">Add a client first <ArrowUpRight className="h-3.5 w-3.5" /></Button>
        </Link>
      </div>
    </div>
  );
}

function RecurringEmptyState() {
  return (
    <div className="py-16 px-8 text-center max-w-md mx-auto">
      <div className="mx-auto mb-5 h-16 w-16 rounded-3xl bg-pearl-200 dark:bg-white/10 flex items-center justify-center">
        <FileText className="h-6 w-6 text-ink/55 dark:text-white/55" strokeWidth={1.5} />
      </div>
      <p className="text-[20px] font-semibold text-ink dark:text-white tracking-[-0.015em]">No recurring templates yet</p>
      <p className="text-[14px] text-ink/60 dark:text-white/60 mt-2 leading-relaxed">
        Set one up for retainers, subscriptions, or anything you bill on a schedule. We'll generate fresh invoices automatically.
      </p>
      <div className="mt-6">
        <Link to="/dashboard/invoices/recurring/new">
          <Button className="gap-1.5"><Plus className="h-3.5 w-3.5" /> New recurring</Button>
        </Link>
      </div>
    </div>
  );
}
